<div class="page-header">
    <div>
        <h4 class="section-title">Playbooks</h4>
        <p class="page-subtitle">
            Step-by-step instructions for one task each (e.g. quote request, booking). The agent sees only the title and
            "when to use" of each enabled playbook and reads the full text when a visitor's request matches it.
        </p>
    </div>
    <div class="flex-spacer"></div>
    <button *ngIf="canCreate" class="btn-primary btn-inline" id="ag-playbooks-add" (click)="openCreate()">
        <i class="fa-solid fa-plus"></i> Add playbook
    </button>
</div>

<div class="info-note" role="note" *ngIf="planOff">
    <i class="fa-solid fa-circle-info"></i>
    <span>The project plan does not include playbooks: the agent does not use them and the customer cannot add or edit them. Support can still manage them here.</span>
</div>
<div class="info-note" role="note" *ngIf="modelWithoutTools">
    <i class="fa-solid fa-circle-info"></i>
    <span>The agent's model ({{ model?.displayName }}) cannot use tools, so the agent answers without playbooks. Choose a model with "Supports tools" to use them.</span>
</div>
<p class="muted playbook-count" *ngIf="playbooks.length || planMax !== null">
    {{ playbooks.length }} playbook{{ playbooks.length === 1 ? '' : 's' }}, {{ enabledCount }} enabled<span *ngIf="planMax !== null"> · plan allows {{ planMax }} per agent</span>
</p>

<div *ngIf="loadError" class="error-text" role="alert">{{ loadError }}</div>
<div *ngIf="listError" class="error-text" role="alert">{{ listError }}</div>

<div class="table-wrapper table-mobile-card">
    <table class="table" id="ag-playbooks-table">
        <thead>
            <tr>
                <th>Title</th>
                <th>Key</th>
                <th>When to use</th>
                <th>Characters</th>
                <th class="status-col">Enabled</th>
                <th class="actions-wide">Actions</th>
            </tr>
        </thead>
        <tbody>
            <tr *ngIf="loading && playbooks.length === 0">
                <td colspan="100%"><div class="no-data">Loading...</div></td>
            </tr>
            <tr *ngIf="!loading && !loadError && playbooks.length === 0">
                <td colspan="100%">
                    <div class="no-data"><i class="fa-solid fa-book-open"></i> No playbooks yet</div>
                </td>
            </tr>
            <tr *ngFor="let p of playbooks">
                <td data-label="Title"><strong dir="auto">{{ p.title }}</strong></td>
                <td data-label="Key"><span class="mono">{{ p.key }}</span></td>
                <td data-label="When to use"><span class="playbook-when" dir="auto" [title]="p.description">{{ p.description }}</span></td>
                <td data-label="Characters">{{ p.chars | number }}</td>
                <td data-label="Enabled" class="status-col">
                    <span [ngClass]="p.enabled ? 'status-active' : 'status-disabled'">{{ p.enabled ? 'Yes' : 'No' }}</span>
                </td>
                <td data-label="Actions" class="actions-wide">
                    <div class="actions-cell">
                        <button class="icon-btn edit" [title]="canUpdate ? 'View / edit' : 'View'" [attr.aria-label]="(canUpdate ? 'Edit ' : 'View ') + p.title"
                            (click)="openEdit(p)">
                            <i class="fa-solid" [ngClass]="canUpdate ? 'fa-pen' : 'fa-eye'"></i>
                        </button>
                        <button *ngIf="canUpdate" class="icon-btn view" [title]="p.enabled ? 'Disable' : 'Enable'"
                            [attr.aria-label]="(p.enabled ? 'Disable ' : 'Enable ') + p.title" [disabled]="toggling[p.id]" (click)="toggleEnabled(p)">
                            <i class="fa-solid" [ngClass]="p.enabled ? 'fa-toggle-on' : 'fa-toggle-off'"></i>
                        </button>
                        <button *ngIf="canDelete" class="icon-btn delete" title="Delete" [attr.aria-label]="'Delete ' + p.title" (click)="remove(p)">
                            <i class="fa-solid fa-trash"></i>
                        </button>
                    </div>
                </td>
            </tr>
        </tbody>
    </table>
</div>

<div *ngIf="editorOpen" class="dialog-overlay">
    <div class="dialog-card" role="dialog" aria-modal="true" aria-labelledby="ag-playbook-dialog-title">
        <div class="dialog-header">
            <div>
                <h3 id="ag-playbook-dialog-title">{{ editing ? (canEditForm ? 'Edit playbook' : 'Playbook') : 'Add playbook' }}</h3>
                <p>Write the steps in Markdown. The agent follows them when the visitor's request matches "when to use".</p>
            </div>
            <button type="button" class="icon-btn back" title="Close" (click)="closeEditor()"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <form class="dialog-body" (ngSubmit)="save()">
            <div *ngIf="editorLoading" class="muted">Loading the playbook...</div>
            <fieldset class="plain-fieldset" [disabled]="!canEditForm || editorLoading">
                <div class="two-col">
                    <div class="form-field">
                        <label for="pb-title">Title</label>
                        <input id="pb-title" name="title" [(ngModel)]="form.title" [maxlength]="titleMax" dir="auto" required placeholder="Quote or demo request" />
                        <span class="hint">{{ form.title.length }} / {{ titleMax }}</span>
                    </div>
                    <div class="form-field">
                        <label for="pb-key">Key</label>
                        <input id="pb-key" name="key" [(ngModel)]="form.key" [maxlength]="keyMax" [readonly]="!!editing" class="mono"
                            placeholder="made from the title" autocomplete="off" spellcheck="false" />
                        <span class="hint">{{ editing ? 'The key cannot change.' : 'Optional: a-z, 0-9, "-" and "_". Empty = made from the title.' }}</span>
                    </div>
                </div>
                <div class="form-field">
                    <label for="pb-description">When to use</label>
                    <textarea id="pb-description" name="description" rows="2" [(ngModel)]="form.description" [maxlength]="descriptionMax" dir="auto" required
                        placeholder="When a visitor wants a price quote, a demo or a project."></textarea>
                    <span class="hint">{{ form.description.length }} / {{ descriptionMax }}. The agent reads this to decide when to open the playbook.</span>
                </div>
                <div class="form-field">
                    <label for="pb-content">Content (Markdown)</label>
                    <textarea id="pb-content" name="content" rows="16" class="playbook-content" [(ngModel)]="form.content" dir="auto" required
                        placeholder="1. Ask for the name and the company.&#10;2. Ask what the project is about.&#10;3. ..."></textarea>
                    <span class="hint" [class.error-text]="contentLength > contentMax" id="pb-content-count">
                        {{ contentLength | number }} / {{ contentMax | number }} characters
                    </span>
                </div>
                <label class="check-row"><input type="checkbox" name="enabled" id="pb-enabled" [(ngModel)]="form.enabled" /> Enabled (the agent may use this playbook)</label>
            </fieldset>
            <div *ngIf="formError" class="error-text" role="alert">{{ formError }}</div>
            <div class="dialog-footer">
                <button type="button" class="btn-light" (click)="closeEditor()">{{ canEditForm ? 'Cancel' : 'Close' }}</button>
                <button *ngIf="canEditForm" type="submit" class="btn-primary" id="ag-playbooks-save" [disabled]="saving || editorLoading">
                    {{ saving ? 'Saving...' : 'Save' }}
                </button>
            </div>
        </form>
    </div>
</div>
